import { PDFDocument, degrees } from 'pdf-lib';
import JSZip from 'jszip';
import { normalizeAngle, marginsToPdfEdges } from './geometry.js';
import { drawPageOverlays } from './overlays.js';
import { getPdfJsDocument } from './pdfPreview.js';
import { visualBounds, visualPointToPdf } from './overlayGeometry.js';
import { assertNonOverlappingReplacements, needsSourceReplacement, validateInPlaceCover } from './sourceEditPolicy.js';
import { hasValidOriginalRegion, repairRegion } from './backgroundRepair.js';

const uuid = () => crypto.randomUUID();

export async function importPdf(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const document = await PDFDocument.load(bytes);
  const sourceId = uuid();
  return {
    source: { id: sourceId, name: file.name, bytes },
    pages: document.getPages().map((page, pageIndex) => ({
      id: uuid(),
      kind: 'pdf',
      sourceId,
      pageIndex,
      rotation: 0,
      baseRotation: page.getRotation().angle,
      overlays: [],
      width: page.getCropBox().width,
      height: page.getCropBox().height,
    })),
  };
}

export function newBlankPage() {
  return { id: uuid(), kind: 'blank', width: 595.28, height: 841.89, rotation: 0, baseRotation: 0, overlays: [] };
}

export { normalizeAngle } from './geometry.js';

/**
 * The page must NOT be flattened when its existing text/images are modified.
 * Convert ONLY the original content's old rectangle to a small, locally repaired
 * PNG patch, then paint that patch onto a copy of the original PDF page.
 * The page's other text, logos, paths, fonts and vector graphics remain intact.
 *
 * This is a visual repair, NOT true deletion of original PDF content streams:
 * hidden text may remain extractable and this must never be sold as redaction.
 */
async function buildLocalizedPatches(descriptor, sources, cache) {
  const originals = assertNonOverlappingReplacements(descriptor.overlays || []).map(x => x.item);
  if (!originals.length) return [];
  const pdfDoc = await getPdfJsDocument(descriptor.sourceId, sources, cache);
  const pdfPage = await pdfDoc.getPage(descriptor.pageIndex + 1);
  const bounds = visualBounds(descriptor);
  const rotation = normalizeAngle((pdfPage.rotate || 0) + descriptor.rotation);
  // Render only to calculate the masks; the final document NEVER uses this as
  // a replacement for the original page.
  const viewport = pdfPage.getViewport({ scale: 2.5, rotation });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d', {willReadFrequently: true});
  if (!ctx) throw new Error('PDF kaynak alanı temizleme katmanı oluşturulamadı.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await pdfPage.render({canvas, canvasContext:ctx, viewport}).promise;
  const original = document.createElement('canvas');
  original.width = canvas.width; original.height = canvas.height;
  original.getContext('2d').drawImage(canvas, 0, 0);
  const sx = canvas.width / bounds.width;
  const sy = canvas.height / bounds.height;
  const patches = [];
  for (const item of originals) {
    const region = item.originalRegion;
    // On export, fail with a clear error instead of silently skipping edits
    // to an original PDF object whose source coordinates are malformed.
    if (!hasValidOriginalRegion(region)) {
      throw new Error('Özgün PDF nesnesinin konum bilgisi geçersiz. Değişikliği geri alın ve yeniden deneyin.');
    }
    // Never take the old text/image with the moved object; rebuild the patch
    // from the pristine page, not from earlier altered patches.
    ctx.clearRect(0,0,canvas.width,canvas.height);
    ctx.drawImage(original,0,0);
    const estimate = repairRegion(ctx,region,sx,sy);
    if (!estimate.safe) throw new Error('Karmaşık desen veya üst üste çizim algılandı. Bu nesnenin kaynağı zarar görmeden silinemiyor; PDF korunması için kaydetme durduruldu.');
    const left = Math.max(0,Math.floor(region.x*sx));
    const top = Math.max(0,Math.floor(region.y*sy));
    const right = Math.min(canvas.width,Math.ceil((region.x+region.width)*sx));
    const bottom = Math.min(canvas.height,Math.ceil((region.y+region.height)*sy));
    const w = right-left, h = bottom-top;
    if(w<1 || h<1) continue;
    const patch = document.createElement('canvas');
    patch.width=w; patch.height=h;
    patch.getContext('2d').drawImage(canvas,left,top,w,h,0,0,w,h);
    patches.push({
      bytes:patch.toDataURL('image/png'),
      region:{ x:left/sx, y:top/sy, width:w/sx, height:h/sy },
    });
  }
  return patches;
}

async function paintLocalizedPatches(pdf, page, patches) {
  const crop = page.getCropBox();
  const angle = normalizeAngle(page.getRotation().angle);
  for(const {bytes,region} of patches) {
    const image=await pdf.embedPng(bytes);
    const origin=visualPointToPdf(region.x,region.y+region.height,crop,angle);
    page.drawImage(image,{
      x:origin.x,y:origin.y,width:region.width,height:region.height,
      rotate:degrees(angle),
    });
  }
}

export async function buildPdf(pages, sources) {
  if (!pages.length) throw new Error('Kaydedilecek sayfa bulunamadı.');
  const output = await PDFDocument.create();
  const loaded = new Map();
  const previewCache = new Map();
  for (const descriptor of pages) {
    if (descriptor.kind === 'blank') {
      const page = output.addPage([descriptor.width, descriptor.height]);
      page.setRotation(degrees(normalizeAngle(descriptor.rotation)));
      await drawPageOverlays(output, page, descriptor.overlays);
      continue;
    }
    const source = sources.get(descriptor.sourceId);
    if (!source) throw new Error('Kaynak PDF bulunamadı. Lütfen dosyayı yeniden açın.');
    if (!loaded.has(source.id)) loaded.set(source.id, await PDFDocument.load(source.bytes));
    const original = loaded.get(source.id);
    const [copiedPage] = await output.copyPages(original, [descriptor.pageIndex]);
    copiedPage.setRotation(degrees(normalizeAngle(copiedPage.getRotation().angle + descriptor.rotation)));
    output.addPage(copiedPage);
    // Even in cover-only mode, validate that source rectangle is unchanged.
    for (const item of descriptor.overlays || []) validateInPlaceCover(item);
    // Paint only small repaired regions; never rasterize the entire PDF page.
    if ((descriptor.overlays || []).some(needsSourceReplacement)) {
      const patches = await buildLocalizedPatches(descriptor, sources, previewCache);
      await paintLocalizedPatches(output,copiedPage,patches);
    }
    await drawPageOverlays(output, copiedPage, descriptor.overlays);
  }
  output.setTitle('PDF Studio');
  output.setProducer('PDF Studio (browser-only)');
  return output.save();
}

export async function createSingleSource(bytes, name) {
  const data = new Uint8Array(bytes);
  const document = await PDFDocument.load(data);
  const sourceId = uuid();
  return {
    source: { id: sourceId, name, bytes: data },
    pages: document.getPages().map((page, pageIndex) => ({
      id: uuid(),
      kind: 'pdf',
      sourceId,
      pageIndex,
      rotation: 0,
      baseRotation: page.getRotation().angle,
      overlays: [],
      width: page.getCropBox().width,
      height: page.getCropBox().height,
    })),
  };
}

export async function cropPage(descriptor, sources, margins) {
  // Crop the base PDF only; editable overlays remain in the UI layer.
  const bytes = await buildPdf([{ ...descriptor, overlays: [] }], sources);
  const document = await PDFDocument.load(bytes);
  const page = document.getPage(0);
  // A second crop must operate *inside* the current CropBox; using MediaBox
  // would make previously cropped content visible again.
  const box = page.getCropBox();
  const edges = marginsToPdfEdges(margins, page.getRotation().angle);
  if (Object.values(edges).some((value) => !Number.isFinite(value) || value < 0 || value > 100)) {
    throw new Error('Kırpma oranları 0–100 arasında olmalıdır.');
  }
  const left = box.width * edges.left / 100;
  const right = box.width * edges.right / 100;
  const top = box.height * edges.top / 100;
  const bottom = box.height * edges.bottom / 100;
  if (left + right >= box.width || top + bottom >= box.height) {
    throw new Error('Kırpma payları sayfanın tamamını kaplayamaz.');
  }
  page.setCropBox(box.x + left, box.y + bottom, box.width - left - right, box.height - top - bottom);
  return createSingleSource(await document.save(), 'Kırpılan Sayfa');
}

export async function zipIndividualPages(pages, sources, name = 'pdf-sayfalar') {
  const zip = new JSZip();
  for (let index = 0; index < pages.length; index++) {
    const bytes = await buildPdf([pages[index]], sources);
    zip.file(`${name}-${String(index + 1).padStart(3, '0')}.pdf`, bytes);
  }
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 5 } });
}

export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function pdfBlob(bytes) {
  return new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
}

export function cleanFileName(text) {
  return (text || 'PDF-Studio').replace(/[<>:"/\\|?*\x00-\x1F]/g, '-').trim().slice(0, 100) || 'PDF-Studio';
}
